import { useMemo } from 'react'

import { useLazyQuery, useQuery } from '@apollo/client'

import {
  BrandViewerAccount,
  RetailerViewerAccount,
  UserInfoQueryDocument,
  UserInfoQueryQuery,
} from '__generated__/atlas-types'

import { UserFromConfig, getCurrentUserFromConfig } from 'utils/config'

import { atlasClient } from 'graphql/client'
import { atlas } from 'graphql/tags'
import {
  ALLOW_EDIT_RETAILER_SETTINGS,
  SUBTYPE_CORE_BRAND,
  SUBTYPE_LITE_BRAND,
} from 'store/currentUser/selectors'

import { useAuth } from '../context/AuthProvider'

atlas`#graphql
   query UserInfoQuery {
    viewingAccountUser {
      id
      tiedWithBuyerHierarchy
      accountUserType
      viewingAccount {
        __typename
        ...on BrandViewerAccount {
          id
          name
          logoUrl
          type
          status
          canTransact
          canViewDataPortal
          integrations
          subType
          enableChat
          accountCreationSource {
            externalSource
          }
        }
        ...on RetailerViewerAccount {
          id
          name
          logoUrl
          type
          status
          enableChat
          assortmentPlanning
          integrations
          canShop
          canFindBrands
          accountCreationSource {
            externalSource
          }
        }
        ...on AdminViewerAccount {
          id
          name
        }
      }
      viewingUser {
        id
        email
        displayName
        dateFormat
        assortmentPlanning
        profileImage
        code
        isAdmin
        isOwner
        loggedInAsAdmin
        lastLogin
        userType
        internalUser
        emailLanguage
        displayLanguage
      }
      accountUserPermissions {
        id
        key
        name
      }
      accounts {
         ...on BrandViewerAccount {
          __typename
          id
          name
        }
        ...on RetailerViewerAccount {
          __typename
          id
          name
        }
        ...on AdminViewerAccount {
          __typename
          id
          name
        }
      }
    }
  }
  `

function mapToUserInfo(
  config: UserFromConfig,
  viewingAccountUser?: UserInfoQueryQuery['viewingAccountUser'],
) {
  return {
    apiHost: config.apiHost,
    isAdmin: viewingAccountUser?.viewingUser.isAdmin || false,
    isOwner: viewingAccountUser?.viewingUser.isOwner || false,
    loggedAsAdmin: viewingAccountUser?.viewingUser.loggedInAsAdmin || false,
    targetAccountId: config.targetAccountId,
    integrations: isRetailerAccount(viewingAccountUser?.viewingAccount)
      ? (viewingAccountUser?.viewingAccount?.integrations?.filter(
          (integration: string | null) =>
            integration === 'RLM' || integration === 'Kellwood',
        ) as string[])
      : [],
    tiedWithBuyerHierarchy: viewingAccountUser?.tiedWithBuyerHierarchy,
    settings: {
      accountUserPermissions: viewingAccountUser?.accountUserPermissions,
    },
    id: viewingAccountUser?.viewingUser?.id,
    accountUserId: viewingAccountUser?.id,
    accountUserType: viewingAccountUser?.accountUserType,
    accounts: (viewingAccountUser?.accounts ?? [])
      .filter((account): account is NonNullable<typeof account> =>
        Boolean(account?.id),
      )
      .map((account) => ({ id: account.id, name: account.name })),
    account: {
      id: viewingAccountUser?.viewingAccount?.id,
      profileName: viewingAccountUser?.viewingAccount?.name,
      logoUrl:
        isBrandAccount(viewingAccountUser?.viewingAccount) ||
        isRetailerAccount(viewingAccountUser?.viewingAccount)
          ? viewingAccountUser?.viewingAccount?.logoUrl
          : null,
      type:
        isBrandAccount(viewingAccountUser?.viewingAccount) ||
        isRetailerAccount(viewingAccountUser?.viewingAccount)
          ? viewingAccountUser?.viewingAccount?.type
          : null,
      status:
        isBrandAccount(viewingAccountUser?.viewingAccount) ||
        isRetailerAccount(viewingAccountUser?.viewingAccount)
          ? viewingAccountUser?.viewingAccount?.status
          : null,
      canTransact: isBrandAccount(viewingAccountUser?.viewingAccount)
        ? viewingAccountUser?.viewingAccount?.canTransact
        : null,
      canViewDataPortal: isBrandAccount(viewingAccountUser?.viewingAccount)
        ? viewingAccountUser?.viewingAccount?.canViewDataPortal
        : null,
      limitedAccess: isBrandAccount(viewingAccountUser?.viewingAccount)
        ? viewingAccountUser?.viewingAccount?.subType === SUBTYPE_LITE_BRAND
        : null,
      coreBrand: isBrandAccount(viewingAccountUser?.viewingAccount)
        ? viewingAccountUser?.viewingAccount?.subType === SUBTYPE_CORE_BRAND
        : null,
      enableChat:
        isBrandAccount(viewingAccountUser?.viewingAccount) ||
        isRetailerAccount(viewingAccountUser?.viewingAccount)
          ? viewingAccountUser?.viewingAccount?.enableChat
          : null,
      canViewAssortments: isRetailerAccount(viewingAccountUser?.viewingAccount)
        ? viewingAccountUser?.viewingAccount?.assortmentPlanning
        : null,
      canShop: isRetailerAccount(viewingAccountUser?.viewingAccount)
        ? viewingAccountUser?.viewingAccount?.canShop
        : null,
      canFindBrands: isRetailerAccount(viewingAccountUser?.viewingAccount)
        ? viewingAccountUser?.viewingAccount?.canFindBrands
        : null,
      origin:
        isBrandAccount(viewingAccountUser?.viewingAccount) ||
        isRetailerAccount(viewingAccountUser?.viewingAccount)
          ? viewingAccountUser?.viewingAccount?.accountCreationSource
              ?.externalSource
          : null,
      subtype: isBrandAccount(viewingAccountUser?.viewingAccount)
        ? viewingAccountUser?.viewingAccount?.subType
        : null,
    },
    assortmentPlanning: viewingAccountUser?.viewingUser?.assortmentPlanning,
    userCanViewRetailerSettings:
      viewingAccountUser?.accountUserPermissions?.some(
        (featureAccess) => featureAccess?.key === ALLOW_EDIT_RETAILER_SETTINGS,
      ),
    isInternal: viewingAccountUser?.viewingUser?.internalUser,
    isLoggedIn: Boolean(viewingAccountUser?.viewingUser?.id),
    name: viewingAccountUser?.viewingUser?.displayName,
    dateFormat: viewingAccountUser?.viewingUser?.dateFormat,
    email: viewingAccountUser?.viewingUser?.email,
    userType: viewingAccountUser?.viewingUser?.userType,
    lastLogin: viewingAccountUser?.viewingUser?.lastLogin,
    emailLanguage: viewingAccountUser?.viewingUser?.emailLanguage,
    displayLanguage: viewingAccountUser?.viewingUser?.displayLanguage,
  }
}

function isBrandAccount(
  account?: UserInfoQueryQuery['viewingAccountUser']['viewingAccount'],
): account is BrandViewerAccount & { __typename: 'BrandViewerAccount' } {
  return account?.__typename === 'BrandViewerAccount'
}

function isRetailerAccount(
  account?: UserInfoQueryQuery['viewingAccountUser']['viewingAccount'],
): account is RetailerViewerAccount & { __typename: 'RetailerViewerAccount' } {
  return account?.__typename === 'RetailerViewerAccount'
}

export const useViewingAccountUserQuery = () => {
  const { isAuthenticated } = useAuth()
  const { data, loading, error } = useQuery(UserInfoQueryDocument, {
    client: atlasClient,
    skip: !isAuthenticated,
  })
  const viewingAccountUser = data?.viewingAccountUser

  const userFromConfig = getCurrentUserFromConfig()

  const userDataFromQuery = useMemo(() => {
    return mapToUserInfo(userFromConfig, viewingAccountUser)
  }, [data])

  return {
    userDataFromQuery,
    userData: viewingAccountUser?.viewingUser,
    loading,
    error,
  }
}

export const useViewingUserInfoLazyQuery = () => {
  const [queryUserInfo] = useLazyQuery(UserInfoQueryDocument, {
    client: atlasClient,
  })

  const userFromConfig = getCurrentUserFromConfig()

  const fetchUserInfo = async () => {
    const res = await queryUserInfo()
    const viewingAccountUser = res?.data?.viewingAccountUser
    return mapToUserInfo(userFromConfig, viewingAccountUser)
  }

  return {
    fetchUserInfo,
  }
}
